import {
  ButtonCta,
  PageGroup,
  ResponsiveImage,
  PageTitle,
} from '@sleepstation/components'
import { BreakingLayout, VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import {
  DIRECTION,
  LAYOUT_ALIGN,
  LAYOUT_CHILD,
  LAYOUT_JUSTIFY,
  LAYOUT_SPACING,
} from '@sleepstation/const'
import { parseToAst } from '@sleepstation/markdown'
import {
  marginBottom,
  marginTop,
  mq,
  paddingTop,
} from '@sleepstation/style-api-helpers'
import { constrainWide } from '@sleepstation/style-mixins'
import PropTypes from 'prop-types'
import React from 'react'
import styled from 'styled-components'

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled.div.attrs({
  className: `Breakout`,
})`
  ${paddingTop([1, 3])}
`

const Body = styled(BreakingLayout).attrs({
  direction: [
    DIRECTION.VERTICAL_REVERSED,
    DIRECTION.VERTICAL_REVERSED,
    DIRECTION.HORIZONTAL,
  ],
  justify: LAYOUT_JUSTIFY.CENTER,
  align: LAYOUT_ALIGN.CENTER,
  spacing: [LAYOUT_SPACING.LARGE, 2, 3, 5],
  child: LAYOUT_CHILD.EQUAL,
})`
  ${constrainWide()}
`

const ImageWrapper = styled.div`
  width: 70%;
  margin: auto;
  ${mq(`mediumUp`)} {
    width: 80%;
  }
`

const Primary = styled(VLayout).attrs({
  align: [LAYOUT_ALIGN.STRETCH, LAYOUT_ALIGN.LEFT],
  spacing: LAYOUT_SPACING.LARGE,
})``

const Secondary = styled.div`
  position: relative;

  /* stylelint-disable-next-line */
  ${mq(`mediumDown`)} {
    width: 100%;
  }
`

const Footer = styled(VLayout).attrs({
  as: `footer`,
  align: [LAYOUT_ALIGN.STRETCH, LAYOUT_ALIGN.LEFT],
})`
  /* stylelint-disable-next-line */
  ${mq(`smallUp`)} {
    max-width: 50%;
  }
  ${marginBottom(2)}
`

const LargePageTitle = styled(PageTitle)`
  display: none;

  ${mq(`mediumUp`)} {
    display: block;
  }
`

const SmallPageTitle = styled(PageTitle)`
  ${mq(`mediumUp`)} {
    display: none;
  }

  ${mq(`xSmallOnly`)} {
    ${marginBottom(1.5)}
  }

  ${mq(`smallOnly`)} {
    ${marginTop(-1)}
  }
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const IntroPage = ({ title, body, image, linkUrl, linkLabel }) => {
  const bodyAst = parseToAst(body)
  const hasButton = !!(linkUrl && linkLabel)

  return (
    <Layout>
      <Body>
        <Primary>
          <LargePageTitle shouldPreventWidows={false}>{title}</LargePageTitle>
          <PageGroup spacing={[0.5, 0.75]}>
            <CopyRenderer htmlAst={bodyAst} />
            {hasButton && (
              <Footer>
                <ButtonCta linkUrl={linkUrl} linkLabel={linkLabel} />
              </Footer>
            )}
          </PageGroup>
        </Primary>
        <Secondary>
          <ImageWrapper>
            <ResponsiveImage
              aspectRatio={image.aspectRatio}
              alt={image.alt}
              src={image.url}
            />
          </ImageWrapper>
        </Secondary>
        <SmallPageTitle shouldPreventWidows={false}>{title}</SmallPageTitle>
      </Body>
    </Layout>
  )
}

IntroPage.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  image: PropTypes.object.isRequired,
  linkLabel: PropTypes.string,
  linkUrl: PropTypes.string,
}

IntroPage.defaultProps = {
  linkLabel: null,
  linkUrl: null,
}

export default IntroPage
